import { createAction } from "@reduxjs/toolkit";
import {
    createRsaaAction,
    type ICreateRsaaActionArgs,
    type IRsaaAction,
    type IRsaaApiError,
} from "../../components/utilities/ReduxUtils";
import { SDS_USE_REALTIME_BUILD_ENABLED } from "../../components/utilities/FeatureGates";
import {
    SdsBuildPhase,
    type InternalSdsBuildRequest,
    type InternalSdsBuildResponse,
    type SdsBuildProgressUpdate,
    type WebSocketSdsBuildRealtimePayload,
    type WebSocketSdsBuildRealtimeUpdate,
} from "../../data/models/schemas";
import {
    createRealtimeRequestAction,
    type RealtimeRequestAction,
} from "../middlewares/realtimeRequest";
import type { AppDispatch, RootState } from "../store";
import type { IUserState } from "../types/IUserState";
import { refreshLicenseInfoMeta } from "../utils/meteringMeta";

export type { SdsBuildProgressUpdate };

export const BUILD_SDS = "BUILD_SDS";
export const SDS_BUILD_REALTIME = "SDS_BUILD_REALTIME";
export const BUILD_SDS_UPDATE = `${BUILD_SDS}_UPDATE`;

const SDS_BUILD_PHASE_VALUES = new Set<string>(Object.values(SdsBuildPhase));

type BuildSdsActionArgs = Omit<
    ICreateRsaaActionArgs<InternalSdsBuildRequest>,
    "body"
> & {
    body: InternalSdsBuildRequest;
};

type SdsBuildDispatchResult =
    | Promise<InternalSdsBuildResponse | undefined>
    | IRsaaAction<InternalSdsBuildRequest>
    | undefined;

type SdsBuildDispatch = (
    dispatch: AppDispatch,
    getState?: () => RootState
) => SdsBuildDispatchResult;

const postBuildSdsSync = createRsaaAction<
    InternalSdsBuildRequest,
    InternalSdsBuildResponse
>({
    path: "sds/build",
    method: "POST",
    type: BUILD_SDS,
    meta: () => refreshLicenseInfoMeta,
});

export const postBuildSdsProgress =
    createAction<SdsBuildProgressUpdate>(BUILD_SDS_UPDATE);

export const postBuildSdsRealtime = {
    update: postBuildSdsProgress,
};

const isSdsBuildProgressUpdate = (
    value: unknown
): value is SdsBuildProgressUpdate => {
    if (!value || typeof value !== "object") {
        return false;
    }
    const candidate = value as Partial<WebSocketSdsBuildRealtimeUpdate>;
    return (
        typeof candidate.phase === "string" &&
        SDS_BUILD_PHASE_VALUES.has(candidate.phase) &&
        typeof candidate.percentComplete === "number" &&
        Number.isFinite(candidate.percentComplete) &&
        typeof candidate.message === "string"
    );
};

const realtimeErrorPayload = (message: string): IRsaaApiError<string> => ({
    error: true,
    response: {
        error: message,
        response: message,
    },
});

const buildRealtimeSds =
    (request: WebSocketSdsBuildRealtimePayload): SdsBuildDispatch =>
    async (
        dispatch: AppDispatch
    ): Promise<InternalSdsBuildResponse | undefined> => {
        dispatch(postBuildSdsSync.request());

        try {
            const realtimeDispatch = dispatch as unknown as (
                action: RealtimeRequestAction<
                    WebSocketSdsBuildRealtimePayload,
                    WebSocketSdsBuildRealtimeUpdate
                >
            ) => Promise<InternalSdsBuildResponse>;
            const response = await realtimeDispatch(
                createRealtimeRequestAction<
                    WebSocketSdsBuildRealtimePayload,
                    WebSocketSdsBuildRealtimeUpdate
                >({
                    type: SDS_BUILD_REALTIME,
                    responseTypePrefix: BUILD_SDS,
                    payload: request,
                    onUpdate: (update) => {
                        if (isSdsBuildProgressUpdate(update)) {
                            dispatch(postBuildSdsProgress(update));
                        }
                    },
                    meta: refreshLicenseInfoMeta,
                })
            );
            dispatch(postBuildSdsSync.success(response));
            return response;
        } catch (error) {
            const message =
                error instanceof Error
                    ? error.message
                    : "Failed to build SDS document.";
            dispatch(postBuildSdsSync.error(realtimeErrorPayload(message)));
            return undefined;
        }
    };

/**
 * Uses the canonical SDSDocument response for both synchronous HTTP and realtime builds.
 * The realtime route may estimate missing chemical properties.
 */
export const postBuildSds = {
    ...postBuildSdsSync,
    action:
        (args: BuildSdsActionArgs): SdsBuildDispatch =>
        (dispatch: AppDispatch, getState?: () => RootState) => {
            if (getState?.().sds.loading) {
                return undefined;
            }
            const user = getState?.().user as IUserState | undefined;
            if (SDS_USE_REALTIME_BUILD_ENABLED(user)) {
                return buildRealtimeSds(args.body)(dispatch, getState);
            }
            return dispatch(
                postBuildSdsSync.action(args)
            ) as IRsaaAction<InternalSdsBuildRequest>;
        },
};
